import { logoUrlForDomain } from '@platform/contracts';
import { Monogram, type MonogramProps, cn } from '@platform/ui';

import { useTenantLogo, useTenantName } from '../lib/console';

/**
 * Empresa en una fila o chip: su logo (el subido en Config o el favicon de su web) y, si no hay,
 * el monograma con el acento propio de la empresa. El color se elige por el slug, así "Sweet
 * Papaya" es del mismo color en el inicio, en la bandeja y en leads.
 */
export function CompanyTag({
  name,
  slug,
  className,
}: {
  name: string;
  slug?: string | null;
  className?: string;
}) {
  const logo = useTenantLogo();
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-2', className)}>
      <Monogram name={name} colorKey={slug ?? name} size="xs" src={logo(slug)} />
      <span className="truncate">{name}</span>
    </span>
  );
}

/** Variante chip (con borde), para metadatos de una tarjeta. */
export function CompanyChip({ name, slug }: { name: string; slug?: string | null }) {
  const logo = useTenantLogo();
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border bg-card py-0.5 pr-2.5 pl-0.5 text-xs font-medium">
      <Monogram
        name={name}
        colorKey={slug ?? name}
        size="xs"
        src={logo(slug)}
        className="rounded-full"
      />
      <span className="truncate">{name}</span>
    </span>
  );
}

/** Logo de una empresa (tenant) por slug, con su monograma de respaldo. */
export function TenantLogo({
  name,
  slug,
  ...props
}: { name: string; slug: string } & Omit<MonogramProps, 'name' | 'colorKey' | 'src'>) {
  const logo = useTenantLogo();
  return <Monogram name={name} colorKey={slug} src={logo(slug)} {...props} />;
}

/**
 * Logo de una tienda (lead): el favicon de su web, que la API busca y guarda (mismo origen). Si la
 * web no tiene ícono usable, quedan las iniciales.
 */
export function StoreLogo({
  name,
  domain,
  ...props
}: { name: string; domain: string | null | undefined } & Omit<MonogramProps, 'name' | 'src'>) {
  return <Monogram name={name} src={logoUrlForDomain(domain)} {...props} />;
}

/** slug → etiqueta con monograma (para celdas de tablas). `null` = tarea de la plataforma. */
export function useTenantTag() {
  const tenantName = useTenantName();
  return (slug: string | null | undefined) => (
    <CompanyTag name={tenantName(slug)} slug={slug ?? 'plataforma'} />
  );
}
